import { useRef, useState } from 'react';

import { Download, Pencil, Plus, Trash2, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useChatbotTools,
  useCreateChatbotTool,
  useDeleteChatbotTool,
  useImportChatbotTools,
  useTestChatbotTool,
  useUpdateChatbotTool,
} from '@/hooks/useChatbotTools';
import { chatbotToolsApi } from '@/lib/api/chatbot-tools.api';

import type { ChatbotTool, ChatbotToolType, TestChatbotToolResponse } from '@repo/shared-types';

interface FormState {
  id?: string;
  name: string;
  description: string;
  tool_type: ChatbotToolType;
  http_method: string;
  url: string;
  headers: string;
  parameters_schema: string;
  response_mapping: string;
  sql_query: string;
  is_mutation: boolean;
  is_active: boolean;
}

const EMPTY: FormState = {
  name: '',
  description: '',
  tool_type: 'sql',
  http_method: 'POST',
  url: '',
  headers: '{}',
  parameters_schema: '{\n  "type": "object",\n  "properties": {},\n  "required": []\n}',
  response_mapping: '{}',
  sql_query: '',
  is_mutation: false,
  is_active: true,
};

function parseJson(value: string, fallback: unknown): unknown {
  try {
    return value.trim() ? JSON.parse(value) : fallback;
  } catch {
    throw new Error('Invalid JSON');
  }
}

const textarea =
  'border-input bg-background ring-offset-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:outline-none';

export const ChatbotToolsPage = () => {
  const { data: tools = [], isLoading } = useChatbotTools();
  const createMut = useCreateChatbotTool();
  const updateMut = useUpdateChatbotTool();
  const deleteMut = useDeleteChatbotTool();
  const testMut = useTestChatbotTool();
  const importMut = useImportChatbotTools();
  const fileRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<TestChatbotToolResponse | null>(null);
  const [testArgs, setTestArgs] = useState('{}');

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const openNew = () => {
    setForm(EMPTY);
    setError('');
    setTestResult(null);
    setTestArgs('{}');
    setOpen(true);
  };

  const openEdit = (tool: ChatbotTool) => {
    setForm({
      id: tool.id,
      name: tool.name,
      description: tool.description,
      tool_type: tool.tool_type,
      http_method: tool.http_method ?? 'POST',
      url: tool.url ?? '',
      headers: JSON.stringify(tool.headers ?? {}, null, 2),
      parameters_schema: JSON.stringify(
        tool.parameters_schema ?? { type: 'object', properties: {} },
        null,
        2,
      ),
      response_mapping: JSON.stringify(tool.response_mapping ?? {}, null, 2),
      sql_query: tool.sql_query ?? '',
      is_mutation: tool.is_mutation,
      is_active: tool.is_active,
    });
    setError('');
    setTestResult(null);
    setTestArgs('{}');
    setOpen(true);
  };

  const buildPayload = () => {
    const base = {
      name: form.name,
      description: form.description,
      tool_type: form.tool_type,
      parameters_schema: parseJson(form.parameters_schema, { type: 'object', properties: {} }),
      is_mutation: form.is_mutation,
      is_active: form.is_active,
    } as Record<string, unknown>;
    if (form.tool_type === 'http') {
      base.http_method = form.http_method;
      base.url = form.url;
      base.headers = parseJson(form.headers, {});
      base.response_mapping = parseJson(form.response_mapping, {});
      base.sql_query = null;
    } else {
      base.sql_query = form.sql_query;
    }
    return base;
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload = buildPayload();
      if (form.id) await updateMut.mutateAsync({ id: form.id, data: payload });
      else await createMut.mutateAsync(payload as never);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  const handleTest = async () => {
    setError('');
    setTestResult(null);
    try {
      const args = parseJson(testArgs, {}) as Record<string, unknown>;
      const result = await testMut.mutateAsync({
        tool_type: form.tool_type,
        http_method: form.http_method as never,
        url: form.url || undefined,
        headers:
          form.tool_type === 'http'
            ? (parseJson(form.headers, {}) as Record<string, string>)
            : undefined,
        parameters_schema: parseJson(form.parameters_schema, {
          type: 'object',
          properties: {},
        }) as never,
        response_mapping:
          form.tool_type === 'http' ? (parseJson(form.response_mapping, {}) as never) : undefined,
        sql_query: form.tool_type === 'sql' ? form.sql_query : undefined,
        args,
      });
      setTestResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    }
  };

  const handleExport = async () => {
    const data = await chatbotToolsApi.export();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chatbot-tools-export.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        const tools = Array.isArray(json) ? json : json.tools;
        await importMut.mutateAsync({ tools });
      } catch {
        setError('Invalid import file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Chatbot Tools</h1>
          <p className="text-muted-foreground text-sm">
            Platform-managed tools the assistant can call. Separate from configuration custom tools.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button onClick={openNew}>
            <Plus className="mr-2 h-4 w-4" /> New Tool
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5}>Loading…</TableCell>
                </TableRow>
              )}
              {!isLoading && tools.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    No tools yet. Create one or import a toolset.
                  </TableCell>
                </TableRow>
              )}
              {tools.map((tool) => (
                <TableRow key={tool.id}>
                  <TableCell>
                    <div className="font-medium">{tool.name}</div>
                    <div className="text-muted-foreground max-w-md truncate text-xs">
                      {tool.description}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{tool.tool_type}</Badge>
                  </TableCell>
                  <TableCell>
                    {tool.is_mutation && <Badge variant="destructive">write</Badge>}
                  </TableCell>
                  <TableCell>{tool.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(tool)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete tool "${tool.name}"?`)) deleteMut.mutate(tool.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit tool' : 'New tool'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Name (snake_case)</label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select
                  value={form.tool_type}
                  onValueChange={(v) => set('tool_type', v as ChatbotToolType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sql">sql (read-only query)</SelectItem>
                    <SelectItem value="http">http (request)</SelectItem>
                    <SelectItem value="pdf">pdf (downloadable table)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Description (shown to the AI)</label>
              <textarea
                className={textarea}
                rows={3}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>

            {form.tool_type === 'http' && (
              <>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-sm font-medium">Method</label>
                    <Select value={form.http_method} onValueChange={(v) => set('http_method', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <label className="text-sm font-medium">URL</label>
                    <Input value={form.url} onChange={(e) => set('url', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Headers (JSON)</label>
                  <textarea
                    className={textarea}
                    rows={2}
                    value={form.headers}
                    onChange={(e) => set('headers', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Response mapping (JSON)</label>
                  <textarea
                    className={textarea}
                    rows={2}
                    value={form.response_mapping}
                    onChange={(e) => set('response_mapping', e.target.value)}
                  />
                </div>
              </>
            )}

            {form.tool_type === 'sql' && (
              <div>
                <label className="text-sm font-medium">SQL query (read-only)</label>
                <textarea
                  className={textarea}
                  rows={6}
                  placeholder="SELECT ... ; leave empty for a freeform tool where the AI passes a `query` arg"
                  value={form.sql_query}
                  onChange={(e) => set('sql_query', e.target.value)}
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Parameters schema (JSON Schema)</label>
              <textarea
                className={textarea}
                rows={6}
                value={form.parameters_schema}
                onChange={(e) => set('parameters_schema', e.target.value)}
              />
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_mutation} onCheckedChange={(v) => set('is_mutation', v)} />
                Write action (preview/confirm)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />
                Active
              </label>
            </div>

            {/* Test */}
            <div className="bg-muted/40 space-y-2 rounded-md border p-3">
              <label className="text-sm font-medium">Test args (JSON)</label>
              <textarea
                className={textarea}
                rows={2}
                value={testArgs}
                onChange={(e) => setTestArgs(e.target.value)}
              />
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testMut.isPending}>
                {testMut.isPending ? 'Running…' : 'Run test'}
              </Button>
              {testResult && (
                <pre className="bg-background max-h-48 overflow-auto rounded border p-2 text-xs">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              )}
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
